// Motion here is guidance, not decoration: it shows a visitor where a section
// sits relative to where they were. That reasoning does not hold for someone who
// asked their system to reduce motion, so every helper checks first.
export const prefersReducedMotion = (): boolean =>
        typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const easeInOutCubic = (progress: number): number =>
        progress < 0.5
            ? 4 * progress * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 3) / 2;

// The browser's own `scroll-behavior: smooth` runs at a fixed, fairly brisk pace
// that a page cannot influence. Animating it ourselves is what makes the tempo
// adjustable — the numbers below are the tempo.
const minDurationMs = 700;
const maxDurationMs = 1400;
const msPerPixel = 0.8;

const durationFor = (distance: number): number =>
        Math.min(maxDurationMs, Math.max(minDurationMs, Math.abs(distance) * msPerPixel));

export const scrollToY = (targetY: number) => {
    const startY = window.scrollY;
    const distance = targetY - startY;

    if (prefersReducedMotion()) {
        window.scrollTo(0, targetY);
        return;
    }

    const duration = durationFor(distance);
    let startTime: number | undefined;

    const step = (now: number) => {
        if (startTime === undefined) {
            startTime = now;
        }
        const progress = Math.min(1, (now - startTime) / duration);
        window.scrollTo(0, startY + distance * easeInOutCubic(progress));
        if (progress < 1) {
            window.requestAnimationFrame(step);
        }
    };

    window.requestAnimationFrame(step);
};

// 'center' for something that appears in place, like a fresh QR code; 'start'
// for jumping to a section, where the heading should land near the top.
const targetYFor = (element: Element, block: 'center' | 'start'): number => {
    const rect = element.getBoundingClientRect();
    const offset = block === 'center'
        ? Math.max(0, (window.innerHeight - rect.height) / 2)
        : 24;
    return window.scrollY + rect.top - offset;
};

export const scrollIntoView = (element: Element | null, block: 'center' | 'start' = 'center') => {
    if (!element) {
        return;
    }
    scrollToY(targetYFor(element, block));
};

// Someone arriving on a deep link was never further up the page, so there is no
// movement to show them — animating here would only delay what they asked for.
export const jumpIntoView = (element: Element | null) => {
    if (!element) {
        return;
    }
    window.scrollTo(0, targetYFor(element, 'start'));
};
